import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, LOGOS } from "../lib/api";
import * as XLSX from "xlsx";
import {
  LogOut, Award, Image as ImageIcon, MessageSquare, Plus, Trash2, Search,
  CheckCircle2, X, Upload, Mail, Phone, User, Calendar, BadgeCheck, Edit,
  Activity, ArrowDownCircle, ArrowUpCircle, FileSpreadsheet, Download, AlertCircle,
  BookOpen, Clock, Wrench, BrainCircuit, Eye, ChevronDown, ChevronUp, KeyRound
} from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("certificates");
  const [admin, setAdmin] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("edf_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    api.get("/auth/me")
      .then((r) => setAdmin(r.data))
      .catch(() => {
        localStorage.removeItem("edf_token");
        navigate("/admin/login");
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("edf_token");
    localStorage.removeItem("edf_admin");
    navigate("/admin/login");
  };

  if (!admin) return <div className="min-h-screen flex items-center justify-center text-[#5C6661]">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#F2EFEB]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGOS.main} alt="EDSW" className="w-10 h-10 object-contain" />
            <div>
              <div className="font-serif-display text-lg text-[#1B4332] font-semibold leading-none">EDSW Admin</div>
              <div className="text-xs text-[#5C6661]">Manage Foundation Content</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-[#222B26]">{admin.name}</div>
              <div className="text-xs text-[#5C6661]">{admin.email}</div>
            </div>
            <button onClick={() => setShowPwd(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm hover:bg-[#1B4332] hover:text-white hover:border-[#1B4332] transition-colors" data-testid="admin-change-password-btn">
              <KeyRound className="w-4 h-4" /> <span className="hidden sm:inline">Change Password</span>
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm hover:bg-[#1B4332] hover:text-white hover:border-[#1B4332] transition-colors" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-2 mb-6 border-b border-border overflow-x-auto" data-testid="admin-tabs">
          <TabBtn active={tab === "certificates"} onClick={() => setTab("certificates")} icon={Award} label="Certificates" testid="tab-certificates" />
          <TabBtn active={tab === "gallery"} onClick={() => setTab("gallery")} icon={ImageIcon} label="Gallery" testid="tab-gallery" />
          <TabBtn active={tab === "courses"} onClick={() => setTab("courses")} icon={BookOpen} label="Courses" testid="tab-courses" />
          <TabBtn active={tab === "services"} onClick={() => setTab("services")} icon={Wrench} label="Services" testid="tab-services" />
          <TabBtn active={tab === "affiliations"} onClick={() => setTab("affiliations")} icon={BadgeCheck} label="Affiliations" testid="tab-affiliations" />
          <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} icon={MessageSquare} label="Messages" testid="tab-messages" />
          <TabBtn active={tab === "test-results"} onClick={() => setTab("test-results")} icon={BrainCircuit} label="Test Results" testid="tab-test-results" />
          <TabBtn active={tab === "activity"} onClick={() => setTab("activity")} icon={Activity} label="Account Activity" testid="tab-activity" />
        </div>

        {tab === "certificates" && <CertificatesPanel />}
        {tab === "gallery" && <GalleryPanel />}
        {tab === "courses" && <CoursesPanel />}
        {tab === "services" && <ServicesPanel />}
        {tab === "affiliations" && <AffiliationsPanel />}
        {tab === "messages" && <MessagesPanel />}
        {tab === "test-results" && <TestResultsPanel />}
        {tab === "activity" && <AccountActivityPanel />}
      </div>
      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
        active ? "border-[#1B4332] text-[#1B4332]" : "border-transparent text-[#5C6661] hover:text-[#1B4332]"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password.length < 8) return setError("New password must be at least 8 characters");
    if (form.new_password !== form.confirm) return setError("New passwords do not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="font-serif-display text-2xl text-[#1B4332]">Change Password</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <div className="p-5 space-y-4">
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-3 text-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Password updated. Use the new password next time you log in.
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#1B4332] text-white hover:bg-[#2D6A4F]">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            <FormField label="Current Password" required type="password" value={form.current_password} onChange={set("current_password")} testid="current-password-input" />
            <FormField label="New Password" required type="password" value={form.new_password} onChange={set("new_password")} placeholder="At least 8 characters" testid="new-password-input" />
            <FormField label="Confirm New Password" required type="password" value={form.confirm} onChange={set("confirm")} testid="confirm-password-input" />
            {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
            <div className="flex justify-end gap-3 pt-3 border-t border-border">
              <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-border">Cancel</button>
              <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#1B4332] text-white hover:bg-[#2D6A4F] disabled:opacity-50" data-testid="change-password-submit">
                {saving ? "Saving..." : "Update Password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// --- helpers ---
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// --- Certificates Panel ---
function CertificatesPanel() {
  const [certs, setCerts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/certificates").then((r) => { setCerts(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = certs.filter((c) =>
    [c.registration_no, c.student_name, c.course].some((x) => x?.toLowerCase().includes(query.toLowerCase()))
  );

  const remove = async (id) => {
    if (!window.confirm("Delete this certificate?")) return;
    await api.delete(`/certificates/${id}`);
    load();
  };

  return (
    <div data-testid="certificates-panel">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-5">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6661]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by reg no, name, course..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-border rounded-xl text-sm focus:outline-none focus:border-[#1B4332]"
            data-testid="cert-search-input"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 border border-[#1B4332] text-[#1B4332] px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#1B4332] hover:text-white transition-colors"
            data-testid="upload-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4" /> Upload Excel
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 bg-[#1B4332] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2D6A4F]"
            data-testid="add-certificate-btn"
          >
            <Plus className="w-4 h-4" /> Add Certificate
          </button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="certificates-table">
            <thead className="bg-[#F2EFEB] text-[#5C6661] text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-5 py-3">Photo</th>
                <th className="text-left px-5 py-3">Registration No.</th>
                <th className="text-left px-5 py-3">Student</th>
                <th className="text-left px-5 py-3">Course</th>
                <th className="text-left px-5 py-3">Grade</th>
                <th className="text-left px-5 py-3">Issue Date</th>
                <th className="text-right px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-10 text-[#5C6661]">Loading...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-[#5C6661]">No certificates found.</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-border hover:bg-[#F9F8F6]" data-testid={`cert-row-${c.registration_no}`}>
                  <td className="px-5 py-3">
                    {c.photo ? (
                      <img src={c.photo} alt={c.student_name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#F2EFEB] flex items-center justify-center"><User className="w-4 h-4 text-[#5C6661]" /></div>
                    )}
                  </td>
                  <td className="px-5 py-3 font-mono text-xs">{c.registration_no}</td>
                  <td className="px-5 py-3 font-medium">{c.student_name}</td>
                  <td className="px-5 py-3">{c.course}</td>
                  <td className="px-5 py-3">{c.grade}</td>
                  <td className="px-5 py-3 text-[#5C6661]">{c.issue_date}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditingCert(c)} className="text-[#1B4332] hover:bg-[#F2EFEB] p-2 rounded-lg" data-testid={`edit-cert-${c.registration_no}`}>
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(c.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-lg" data-testid={`delete-cert-${c.registration_no}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <CertificateForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {editingCert && <CertificateForm initial={editingCert} onClose={() => setEditingCert(null)} onSaved={() => { setEditingCert(null); load(); }} />}
      {showUpload && <ExcelUploadModal onClose={() => setShowUpload(false)} onSaved={() => { setShowUpload(false); load(); }} />}
    </div>
  );
}

// --- Excel Upload Modal ---
const EXCEL_COLUMNS = [
  { key: "registration_no", label: "Registration No", required: true },
  { key: "student_name",    label: "Student Name",    required: true },
  { key: "father_name",     label: "Father Name",     required: false },
  { key: "course",          label: "Course",          required: true },
  { key: "duration",        label: "Duration",        required: false },
  { key: "grade",           label: "Grade",           required: true },
  { key: "issue_date",      label: "Issue Date",      required: true },
];

function downloadTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([
    EXCEL_COLUMNS.map((c) => c.label),
    ["REG001", "John Doe", "Ram Doe", "DCA", "6 Months", "A+", "2024-01-15"],
  ]);
  ws["!cols"] = EXCEL_COLUMNS.map(() => ({ wch: 20 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Certificates");
  XLSX.writeFile(wb, "certificate_template.xlsx");
}

function parseExcelRows(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        const colMap = {
          "Registration No": "registration_no",
          "Student Name":    "student_name",
          "Father Name":     "father_name",
          "Course":          "course",
          "Duration":        "duration",
          "Grade":           "grade",
          "Issue Date":      "issue_date",
          // also accept raw key names
          "registration_no": "registration_no",
          "student_name":    "student_name",
          "father_name":     "father_name",
          "course":          "course",
          "duration":        "duration",
          "grade":           "grade",
          "issue_date":      "issue_date",
        };
        const parsed = rows.map((row, idx) => {
          const rec = {};
          for (const [rawKey, val] of Object.entries(row)) {
            const mapped = colMap[rawKey.trim()];
            if (mapped) rec[mapped] = String(val).trim();
          }
          // normalise date — Excel may give a serial number
          if (rec.issue_date && /^\d{5}$/.test(rec.issue_date)) {
            const d = XLSX.SSF.parse_date_code(parseInt(rec.issue_date));
            rec.issue_date = `${d.y}-${String(d.m).padStart(2,"0")}-${String(d.d).padStart(2,"0")}`;
          }
          const errors = EXCEL_COLUMNS.filter((c) => c.required && !rec[c.key]).map((c) => c.label);
          return { ...rec, _row: idx + 2, _errors: errors };
        }).filter((r) => Object.keys(r).length > 2); // skip empty rows
        resolve(parsed);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

function ExcelUploadModal({ onClose, onSaved }) {
  const fileRef = useRef();
  const [rows, setRows] = useState(null);
  const [parseError, setParseError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError("");
    setRows(null);
    setResult(null);
    try {
      const parsed = await parseExcelRows(file);
      if (parsed.length === 0) { setParseError("No data rows found in the file."); return; }
      setRows(parsed);
    } catch {
      setParseError("Could not read file. Make sure it is a valid .xlsx or .csv.");
    }
  };

  const validRows = rows?.filter((r) => r._errors.length === 0) ?? [];
  const invalidRows = rows?.filter((r) => r._errors.length > 0) ?? [];

  const submit = async () => {
    if (validRows.length === 0) return;
    setUploading(true);
    try {
      const payload = validRows.map(({ _row, _errors, ...rest }) => rest);
      const { data } = await api.post("/certificates/bulk", payload);
      setResult(data);
    } catch (err) {
      setParseError(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="excel-upload-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-6 h-6 text-[#1B4332]" />
            <h3 className="font-serif-display text-2xl text-[#1B4332]">Upload Excel</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {/* Template download */}
          <div className="flex items-center justify-between bg-[#F2EFEB] rounded-xl px-4 py-3">
            <div>
              <p className="text-sm font-medium text-[#222B26]">Download template first</p>
              <p className="text-xs text-[#5C6661] mt-0.5">
                Columns: {EXCEL_COLUMNS.map((c) => c.label + (c.required ? "*" : "")).join(", ")}
              </p>
            </div>
            <button
              onClick={downloadTemplate}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1B4332] border border-[#1B4332] px-4 py-2 rounded-full hover:bg-[#1B4332] hover:text-white transition-colors"
            >
              <Download className="w-4 h-4" /> Template
            </button>
          </div>

          {/* File picker */}
          {!result && (
            <div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} className="hidden" data-testid="excel-file-input" />
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-border rounded-xl py-10 text-center hover:border-[#1B4332] transition-colors group"
              >
                <Upload className="w-8 h-8 mx-auto text-[#5C6661] group-hover:text-[#1B4332]" />
                <p className="mt-2 text-sm text-[#5C6661] group-hover:text-[#1B4332]">
                  Click to select .xlsx / .xls / .csv
                </p>
              </button>
            </div>
          )}

          {parseError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {parseError}
            </div>
          )}

          {/* Preview table */}
          {rows && !result && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <span className="text-green-700 font-semibold">{validRows.length} valid</span>
                {invalidRows.length > 0 && (
                  <span className="text-red-600 font-semibold">{invalidRows.length} invalid (will be skipped)</span>
                )}
                <span className="text-[#5C6661]">· {rows.length} total rows parsed</span>
              </div>

              {invalidRows.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700 space-y-1">
                  {invalidRows.map((r) => (
                    <div key={r._row}>Row {r._row}: missing {r._errors.join(", ")}</div>
                  ))}
                </div>
              )}

              <div className="border border-border rounded-xl overflow-hidden">
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F2EFEB] text-[#5C6661] uppercase tracking-widest sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Row</th>
                        {EXCEL_COLUMNS.map((c) => (
                          <th key={c.key} className="text-left px-3 py-2">{c.label}</th>
                        ))}
                        <th className="text-left px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r._row} className={`border-t border-border ${r._errors.length ? "bg-red-50" : ""}`}>
                          <td className="px-3 py-2 text-[#5C6661]">{r._row}</td>
                          {EXCEL_COLUMNS.map((c) => (
                            <td key={c.key} className={`px-3 py-2 ${c.required && !r[c.key] ? "text-red-600 font-semibold" : ""}`}>
                              {r[c.key] || <span className="text-[#5C6661]">—</span>}
                            </td>
                          ))}
                          <td className="px-3 py-2">
                            {r._errors.length === 0
                              ? <span className="text-green-700">✓ OK</span>
                              : <span className="text-red-600">✗ Skip</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800">
                <p className="font-semibold text-base mb-1">Upload complete</p>
                <p>{result.inserted} certificate(s) added successfully.</p>
                {result.skipped.length > 0 && (
                  <p className="mt-1 text-yellow-700">{result.skipped.length} skipped (duplicate reg no): {result.skipped.join(", ")}</p>
                )}
              </div>
              <button onClick={onSaved} className="w-full py-2.5 rounded-full bg-[#1B4332] text-white text-sm font-semibold hover:bg-[#2D6A4F]">
                Done
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {rows && !result && (
          <div className="p-5 border-t border-border flex justify-end gap-3">
            <button onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-border">Cancel</button>
            <button
              onClick={submit}
              disabled={uploading || validRows.length === 0}
              className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#1B4332] text-white hover:bg-[#2D6A4F] disabled:opacity-50"
              data-testid="excel-upload-submit"
            >
              {uploading ? "Uploading..." : `Upload ${validRows.length} Certificate${validRows.length !== 1 ? "s" : ""}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CertificateForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial
      ? { registration_no: initial.registration_no, student_name: initial.student_name,
          father_name: initial.father_name || "", course: initial.course,
          duration: initial.duration || "", issue_date: initial.issue_date,
          grade: initial.grade, photo: initial.photo || "" }
      : { registration_no: "", student_name: "", father_name: "", course: "", duration: "",
          issue_date: new Date().toISOString().split("T")[0], grade: "", photo: "" }
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      setError("Photo must be under 2MB");
      return;
    }
    const b64 = await fileToBase64(f);
    setForm({ ...form, photo: b64 });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/certificates/${initial.id}`, form);
      } else {
        await api.post("/certificates", form);
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="cert-form-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-white">
          <h3 className="font-serif-display text-2xl text-[#1B4332]">{initial ? "Edit" : "Add"} Certificate</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted" data-testid="cert-form-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Registration No." required value={form.registration_no} onChange={set("registration_no")} testid="cert-regno-input" mono />
            <FormField label="Student Name" required value={form.student_name} onChange={set("student_name")} testid="cert-name-input" />
            <FormField label="Father's Name" value={form.father_name} onChange={set("father_name")} testid="cert-father-input" />
            <FormField label="Course" required value={form.course} onChange={set("course")} testid="cert-course-input" />
            <FormField label="Duration" value={form.duration} onChange={set("duration")} placeholder="e.g. 6 Months" testid="cert-duration-input" />
            <FormField label="Grade" required value={form.grade} onChange={set("grade")} placeholder="e.g. A+" testid="cert-grade-input" />
            <FormField label="Issue Date" required type="date" value={form.issue_date} onChange={set("issue_date")} testid="cert-date-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Student Photo (optional, &lt;2MB)</label>
            <div className="mt-1.5 flex items-center gap-4">
              {form.photo ? (
                <img src={form.photo} alt="preview" className="w-20 h-20 rounded-xl object-cover border border-border" />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-[#F2EFEB] flex items-center justify-center">
                  <User className="w-8 h-8 text-[#5C6661]" />
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" data-testid="cert-photo-input" />
              <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-xl text-sm hover:bg-muted">
                <Upload className="w-4 h-4" /> Upload Photo
              </button>
              {form.photo && (
                <button type="button" onClick={() => setForm({ ...form, photo: "" })} className="text-sm text-red-600">Remove</button>
              )}
            </div>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}

          <div className="flex justify-end gap-3 pt-3 border-t border-border">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-border">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#1B4332] text-white hover:bg-[#2D6A4F] disabled:opacity-50" data-testid="cert-form-submit">
              {saving ? "Saving..." : "Save Certificate"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormField({ label, required, value, onChange, type = "text", placeholder, testid, mono }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">
        {label}{required && " *"}
      </label>
      <input
        required={required}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`mt-1.5 w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#1B4332] ${mono ? "font-mono" : ""}`}
        data-testid={testid}
      />
    </div>
  );
}

// --- Gallery Panel ---
function GalleryPanel() {
  const [photos, setPhotos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/gallery").then((r) => { setPhotos(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    await api.delete(`/gallery/${id}`);
    load();
  };

  return (
    <div data-testid="gallery-panel">
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-[#5C6661]">{photos.length} photo(s) in gallery</p>
        <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 bg-[#1B4332] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2D6A4F]" data-testid="add-photo-btn">
          <Plus className="w-4 h-4" /> Add Photo
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-[#5C6661]">Loading...</div>
      ) : photos.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-border rounded-2xl py-20 text-center text-[#5C6661]">
          <ImageIcon className="w-10 h-10 mx-auto opacity-40" />
          <p className="mt-3 text-sm">No photos yet. Click "Add Photo" to upload.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((p) => (
            <div key={p.id} className="bg-white border border-border rounded-2xl overflow-hidden group" data-testid={`gallery-admin-${p.id}`}>
              <div className="aspect-square relative">
                <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <div className="font-medium text-sm truncate">{p.title}</div>
                {p.caption && <div className="text-xs text-[#5C6661] truncate">{p.caption}</div>}
                <button onClick={() => remove(p.id)} className="mt-2 inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <GalleryForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function GalleryForm({ onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", caption: "", image: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      setError("Image must be under 3MB");
      return;
    }
    const b64 = await fileToBase64(f);
    setForm({ ...form, image: b64 });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image) { setError("Please upload an image"); return; }
    setSaving(true);
    setError("");
    try {
      await api.post("/gallery", form);
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to upload");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="gallery-form-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="font-serif-display text-2xl text-[#1B4332]">Add Gallery Photo</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <FormField label="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} testid="photo-title-input" />
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Caption</label>
            <textarea value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} rows={2} className="mt-1.5 w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#1B4332] resize-none" data-testid="photo-caption-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Image (&lt;3MB) *</label>
            <div className="mt-1.5 flex items-center gap-4">
              {form.image ? (
                <img src={form.image} alt="preview" className="w-24 h-24 rounded-xl object-cover border border-border" />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-[#F2EFEB] flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-[#5C6661]" />
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" data-testid="photo-file-input" />
              <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-xl text-sm hover:bg-muted">
                <Upload className="w-4 h-4" /> {form.image ? "Replace" : "Upload Image"}
              </button>
            </div>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}

          <div className="flex justify-end gap-3 pt-3 border-t border-border">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-border">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#1B4332] text-white hover:bg-[#2D6A4F] disabled:opacity-50" data-testid="photo-form-submit">
              {saving ? "Uploading..." : "Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Messages Panel ---
function MessagesPanel() {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/contact").then((r) => { setMsgs(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const markRead = async (id) => {
    await api.put(`/contact/${id}/read`);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/contact/${id}`);
    load();
  };

  return (
    <div data-testid="messages-panel">
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-[#5C6661]">
          {msgs.length} total • {msgs.filter((m) => !m.read).length} unread
        </p>
      </div>

      {loading ? (
        <div className="text-center py-10 text-[#5C6661]">Loading...</div>
      ) : msgs.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-border rounded-2xl py-20 text-center text-[#5C6661]">
          <MessageSquare className="w-10 h-10 mx-auto opacity-40" />
          <p className="mt-3 text-sm">No messages yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {msgs.map((m) => (
            <div key={m.id} className={`bg-white border rounded-2xl p-5 ${m.read ? "border-border" : "border-[#E07A5F]"}`} data-testid={`msg-${m.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-[#222B26]">{m.name}</h4>
                    {!m.read && <span className="text-[10px] uppercase tracking-widest bg-[#E07A5F] text-white px-2 py-0.5 rounded-full">New</span>}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-[#5C6661]">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {m.email}</span>
                    {m.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {m.phone}</span>}
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(m.created_at).toLocaleString()}</span>
                  </div>
                  {m.subject && <div className="mt-3 text-sm font-medium">{m.subject}</div>}
                  <p className="mt-2 text-sm text-[#5C6661] leading-relaxed whitespace-pre-wrap">{m.message}</p>
                </div>
                <div className="flex flex-col gap-2">
                  {!m.read && (
                    <button onClick={() => markRead(m.id)} className="p-2 rounded-lg hover:bg-green-50 text-green-700" title="Mark as read">
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => remove(m.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// --- Account Activity Panel ---
function AccountActivityPanel() {
  const [subTab, setSubTab] = useState("deposits");

  return (
    <div data-testid="activity-panel">
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setSubTab("deposits")}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${
            subTab === "deposits"
              ? "bg-[#1B4332] text-white border-[#1B4332]"
              : "border-border text-[#5C6661] hover:border-[#1B4332] hover:text-[#1B4332]"
          }`}
        >
          <ArrowDownCircle className="w-4 h-4" /> Deposit History
        </button>
        <button
          onClick={() => setSubTab("trades")}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${
            subTab === "trades"
              ? "bg-[#1B4332] text-white border-[#1B4332]"
              : "border-border text-[#5C6661] hover:border-[#1B4332] hover:text-[#1B4332]"
          }`}
        >
          <ArrowUpCircle className="w-4 h-4" /> Trade History
        </button>
      </div>

      {subTab === "deposits" && <DepositsPanel />}
      {subTab === "trades" && <TradesPanel />}
    </div>
  );
}

function DepositsPanel() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/deposits").then((r) => { setRecords(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this deposit record?")) return;
    await api.delete(`/deposits/${id}`);
    load();
  };

  const total = records.reduce((s, r) => s + r.amount, 0);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="text-sm text-[#5C6661]">
          {records.length} record(s) &nbsp;·&nbsp;
          <span className="font-semibold text-[#1B4332]">Total: ₹{total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 bg-[#1B4332] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2D6A4F]"
          data-testid="add-deposit-btn"
        >
          <Plus className="w-4 h-4" /> Add Deposit
        </button>
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="deposits-table">
            <thead className="bg-[#F2EFEB] text-[#5C6661] text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-5 py-3">Date</th>
                <th className="text-left px-5 py-3">Donor</th>
                <th className="text-left px-5 py-3">Source</th>
                <th className="text-left px-5 py-3">Ref No.</th>
                <th className="text-right px-5 py-3">Amount (₹)</th>
                <th className="text-left px-5 py-3">Notes</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center py-10 text-[#5C6661]">Loading...</td></tr>}
              {!loading && records.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-[#5C6661]">No deposit records yet.</td></tr>
              )}
              {records.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-[#F9F8F6]">
                  <td className="px-5 py-3 text-[#5C6661] whitespace-nowrap">{r.date}</td>
                  <td className="px-5 py-3 font-medium">{r.donor_name}</td>
                  <td className="px-5 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">{r.source}</span>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-[#5C6661]">{r.reference_no || "—"}</td>
                  <td className="px-5 py-3 text-right font-semibold text-green-700">
                    {r.amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-5 py-3 text-[#5C6661] max-w-xs truncate">{r.notes || "—"}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => remove(r.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <DepositForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function DepositForm({ onClose, onSaved }) {
  const [form, setForm] = useState({
    amount: "", donor_name: "", source: "Individual",
    date: new Date().toISOString().split("T")[0], reference_no: "", notes: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.post("/deposits", { ...form, amount: parseFloat(form.amount) });
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-white">
          <h3 className="font-serif-display text-2xl text-[#1B4332]">Add Deposit</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Amount (₹)" required type="number" value={form.amount} onChange={set("amount")} placeholder="0.00" />
            <FormField label="Date" required type="date" value={form.date} onChange={set("date")} />
            <FormField label="Donor Name" required value={form.donor_name} onChange={set("donor_name")} />
            <div>
              <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Source *</label>
              <select
                required value={form.source} onChange={set("source")}
                className="mt-1.5 w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#1B4332] bg-white"
              >
                {["Individual", "Organization", "Government Grant", "CSR", "Other"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <FormField label="Reference No." value={form.reference_no} onChange={set("reference_no")} placeholder="Optional" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Notes</label>
            <textarea value={form.notes} onChange={set("notes")} rows={2} className="mt-1.5 w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#1B4332] resize-none" />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
          <div className="flex justify-end gap-3 pt-3 border-t border-border">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-border">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#1B4332] text-white hover:bg-[#2D6A4F] disabled:opacity-50">
              {saving ? "Saving..." : "Save Deposit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TradesPanel() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/trades").then((r) => { setRecords(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this trade record?")) return;
    await api.delete(`/trades/${id}`);
    load();
  };

  const total = records.reduce((s, r) => s + r.amount, 0);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="text-sm text-[#5C6661]">
          {records.length} record(s) &nbsp;·&nbsp;
          <span className="font-semibold text-red-700">Total Spent: ₹{total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 bg-[#1B4332] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2D6A4F]"
          data-testid="add-trade-btn"
        >
          <Plus className="w-4 h-4" /> Add Trade
        </button>
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="trades-table">
            <thead className="bg-[#F2EFEB] text-[#5C6661] text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-5 py-3">Date</th>
                <th className="text-left px-5 py-3">Description</th>
                <th className="text-left px-5 py-3">Category</th>
                <th className="text-left px-5 py-3">Ref No.</th>
                <th className="text-right px-5 py-3">Amount (₹)</th>
                <th className="text-left px-5 py-3">Notes</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center py-10 text-[#5C6661]">Loading...</td></tr>}
              {!loading && records.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-[#5C6661]">No trade records yet.</td></tr>
              )}
              {records.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-[#F9F8F6]">
                  <td className="px-5 py-3 text-[#5C6661] whitespace-nowrap">{r.date}</td>
                  <td className="px-5 py-3 font-medium">{r.description}</td>
                  <td className="px-5 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-800">{r.category}</span>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-[#5C6661]">{r.reference_no || "—"}</td>
                  <td className="px-5 py-3 text-right font-semibold text-red-700">
                    {r.amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-5 py-3 text-[#5C6661] max-w-xs truncate">{r.notes || "—"}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => remove(r.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <TradeForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function TradeForm({ onClose, onSaved }) {
  const [form, setForm] = useState({
    amount: "", description: "", category: "Education",
    date: new Date().toISOString().split("T")[0], reference_no: "", notes: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.post("/trades", { ...form, amount: parseFloat(form.amount) });
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-white">
          <h3 className="font-serif-display text-2xl text-[#1B4332]">Add Trade</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Amount (₹)" required type="number" value={form.amount} onChange={set("amount")} placeholder="0.00" />
            <FormField label="Date" required type="date" value={form.date} onChange={set("date")} />
            <FormField label="Description" required value={form.description} onChange={set("description")} />
            <div>
              <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Category *</label>
              <select
                required value={form.category} onChange={set("category")}
                className="mt-1.5 w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#1B4332] bg-white"
              >
                {["Education", "Healthcare", "Women Empowerment", "Community Development", "Admin", "Other"].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <FormField label="Reference No." value={form.reference_no} onChange={set("reference_no")} placeholder="Optional" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Notes</label>
            <textarea value={form.notes} onChange={set("notes")} rows={2} className="mt-1.5 w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#1B4332] resize-none" />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
          <div className="flex justify-end gap-3 pt-3 border-t border-border">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-border">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#1B4332] text-white hover:bg-[#2D6A4F] disabled:opacity-50">
              {saving ? "Saving..." : "Save Trade"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Services Panel ---
const SERVICE_ICONS = [
  "Monitor","Award","Calculator","Globe","FileText","Users",
  "BookOpen","Star","Briefcase","Lightbulb","HeartHandshake","Laptop","Wrench",
];

function ServicesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/services").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await api.delete(`/services/${id}`);
    load();
  };

  return (
    <div data-testid="services-panel">
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-[#5C6661]">{items.length} service(s) — shown on the home page Services section</p>
        <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-[#1B4332] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2D6A4F]" data-testid="add-service-btn">
          <Plus className="w-4 h-4" /> Add Service
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-[#5C6661]">Loading...</div>
      ) : items.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-border rounded-2xl py-20 text-center text-[#5C6661]">
          <Wrench className="w-10 h-10 mx-auto opacity-40" />
          <p className="mt-3 text-sm">No services yet. Add one to show it on the home page.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <div key={s.id} className="bg-white border border-border rounded-2xl p-5" data-testid={`service-row-${s.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-[#F2EFEB] text-[#5C6661]">{s.icon || "—"}</span>
              </div>
              <h3 className="font-semibold text-[#1B4332]">{s.title}</h3>
              <p className="mt-1.5 text-xs text-[#5C6661] leading-relaxed line-clamp-3">{s.description}</p>
              <div className="flex gap-3 mt-4 pt-3 border-t border-border">
                <button onClick={() => setEditing(s)} className="inline-flex items-center gap-1 text-xs text-[#1B4332] hover:underline" data-testid={`edit-service-${s.id}`}>
                  <Edit className="w-3 h-3" /> Edit
                </button>
                <button onClick={() => remove(s.id)} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline" data-testid={`delete-service-${s.id}`}>
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <ServiceForm
          initial={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ServiceForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || { title: "", description: "", icon: "Star" }
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) {
      setError("Title and description are required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (initial?.id) {
        await api.put(`/services/${initial.id}`, form);
      } else {
        await api.post("/services", form);
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="service-form-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-white">
          <h3 className="font-serif-display text-2xl text-[#1B4332]">{initial ? "Edit" : "Add"} Service</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <FormField label="Service Title" required value={form.title} onChange={set("title")} placeholder="e.g. Computer Training" testid="service-title-input" />
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Icon</label>
            <select
              value={form.icon}
              onChange={set("icon")}
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-border bg-[#F9F8F6] text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]"
              data-testid="service-icon-select"
            >
              {SERVICE_ICONS.map((ic) => (
                <option key={ic} value={ic}>{ic}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Description *</label>
            <textarea
              required
              rows={3}
              value={form.description}
              onChange={set("description")}
              placeholder="Brief description of this service..."
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-border bg-[#F9F8F6] text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]"
              data-testid="service-desc-input"
            />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
          <div className="flex justify-end gap-3 pt-3 border-t border-border">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-border">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#1B4332] text-white hover:bg-[#2D6A4F] disabled:opacity-50" data-testid="service-form-submit">
              {saving ? "Saving..." : (initial ? "Update" : "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Courses Panel ---
function CoursesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/courses").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this course?")) return;
    await api.delete(`/courses/${id}`);
    load();
  };

  return (
    <div data-testid="courses-panel">
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-[#5C6661]">{items.length} course(s) — shown on the home page Courses section</p>
        <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-[#1B4332] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2D6A4F]" data-testid="add-course-btn">
          <Plus className="w-4 h-4" /> Add Course
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-[#5C6661]">Loading...</div>
      ) : items.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-border rounded-2xl py-20 text-center text-[#5C6661]">
          <BookOpen className="w-10 h-10 mx-auto opacity-40" />
          <p className="mt-3 text-sm">No courses yet. Add one to show it on the home page.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-border rounded-2xl p-5" data-testid={`course-row-${c.id}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <span className="inline-flex items-center text-xs font-mono px-3 py-1 rounded-full bg-[#1B4332] text-white">{c.code}</span>
                <div className="flex items-center gap-1 text-xs text-[#5C6661]">
                  <Clock className="w-3.5 h-3.5" /> {c.duration}
                </div>
              </div>
              <h3 className="font-semibold text-[#1B4332] leading-snug">{c.name}</h3>
              <p className="mt-2 text-xs text-[#5C6661] leading-relaxed line-clamp-3">{c.description}</p>
              <div className="flex gap-3 mt-4 pt-3 border-t border-border">
                <button onClick={() => setEditing(c)} className="inline-flex items-center gap-1 text-xs text-[#1B4332] hover:underline" data-testid={`edit-course-${c.id}`}>
                  <Edit className="w-3 h-3" /> Edit
                </button>
                <button onClick={() => remove(c.id)} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline" data-testid={`delete-course-${c.id}`}>
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <CourseForm
          initial={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function CourseForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || { code: "", name: "", duration: "", description: "" }
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name || !form.duration || !form.description) {
      setError("All fields are required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (initial?.id) {
        await api.put(`/courses/${initial.id}`, form);
      } else {
        await api.post("/courses", form);
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="course-form-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-white">
          <h3 className="font-serif-display text-2xl text-[#1B4332]">{initial ? "Edit" : "Add"} Course</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <FormField label="Course Code" required value={form.code} onChange={set("code")} placeholder="e.g. DCA" testid="course-code-input" />
          <FormField label="Course Name" required value={form.name} onChange={set("name")} placeholder="e.g. Diploma in Computer Application" testid="course-name-input" />
          <FormField label="Duration" required value={form.duration} onChange={set("duration")} placeholder="e.g. 6 Months" testid="course-duration-input" />
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Description *</label>
            <textarea
              required
              rows={3}
              value={form.description}
              onChange={set("description")}
              placeholder="Brief description of what the course covers..."
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-border bg-[#F9F8F6] text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]"
              data-testid="course-desc-input"
            />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
          <div className="flex justify-end gap-3 pt-3 border-t border-border">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-border">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#1B4332] text-white hover:bg-[#2D6A4F] disabled:opacity-50" data-testid="course-form-submit">
              {saving ? "Saving..." : (initial ? "Update" : "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Affiliations Panel ---
function AffiliationsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, obj = edit

  const load = () => {
    setLoading(true);
    api.get("/affiliations").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this affiliation?")) return;
    await api.delete(`/affiliations/${id}`);
    load();
  };

  return (
    <div data-testid="affiliations-panel">
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-[#5C6661]">{items.length} affiliation(s) — shown on home page, why-choose-us strip & footer</p>
        <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-[#1B4332] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2D6A4F]" data-testid="add-affiliation-btn">
          <Plus className="w-4 h-4" /> Add Affiliation
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-[#5C6661]">Loading...</div>
      ) : items.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-border rounded-2xl py-20 text-center text-[#5C6661]">
          <BadgeCheck className="w-10 h-10 mx-auto opacity-40" />
          <p className="mt-3 text-sm">No affiliations yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((a) => (
            <div key={a.id} className="bg-white border border-border rounded-2xl p-5 flex gap-4 items-start" data-testid={`affiliation-row-${a.id}`}>
              <img src={a.logo} alt={a.name} className="w-20 h-20 object-contain shrink-0 bg-[#F9F8F6] rounded-xl p-2" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#222B26]">{a.name}</div>
                {a.short_name && <div className="text-xs text-[#5C6661]">{a.short_name}</div>}
                {a.link && <div className="text-xs text-[#E07A5F] truncate mt-1">{a.link}</div>}
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setEditing(a)} className="inline-flex items-center gap-1 text-xs text-[#1B4332] hover:underline" data-testid={`edit-affiliation-${a.id}`}>
                    <Edit className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => remove(a.id)} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline" data-testid={`delete-affiliation-${a.id}`}>
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <AffiliationForm
          initial={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function AffiliationForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || { name: "", short_name: "", logo: "", link: "", order: 0 }
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1 * 1024 * 1024) {
      setError("Logo must be under 1MB");
      return;
    }
    const b64 = await fileToBase64(f);
    setForm({ ...form, logo: b64 });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.logo) { setError("Please upload a logo"); return; }
    setSaving(true);
    setError("");
    try {
      if (initial?.id) {
        await api.put(`/affiliations/${initial.id}`, form);
      } else {
        await api.post("/affiliations", form);
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="affiliation-form-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-white">
          <h3 className="font-serif-display text-2xl text-[#1B4332]">{initial ? "Edit" : "Add"} Affiliation</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <FormField label="Full Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. ISO 9001:2015 Certified" testid="aff-name-input" />
          <FormField label="Short Name (badge label)" value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} placeholder="e.g. ISO 9001:2015" testid="aff-short-input" />
          <FormField label="Link (optional)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://..." testid="aff-link-input" />
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Display Order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} testid="aff-order-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Logo (&lt;1MB) *</label>
            <div className="mt-1.5 flex items-center gap-4">
              {form.logo ? (
                <img src={form.logo} alt="preview" className="w-24 h-24 rounded-xl object-contain border border-border bg-[#F9F8F6] p-2" />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-[#F2EFEB] flex items-center justify-center">
                  <BadgeCheck className="w-8 h-8 text-[#5C6661]" />
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={handleLogo} className="hidden" data-testid="aff-logo-input" />
              <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-xl text-sm hover:bg-muted">
                <Upload className="w-4 h-4" /> {form.logo ? "Replace" : "Upload Logo"}
              </button>
            </div>
            <p className="text-xs text-[#5C6661] mt-2">
              Tip: PNG/SVG with transparent background works best. Square images (1:1) display cleanest.
            </p>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}

          <div className="flex justify-end gap-3 pt-3 border-t border-border">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-border">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#1B4332] text-white hover:bg-[#2D6A4F] disabled:opacity-50" data-testid="aff-form-submit">
              {saving ? "Saving..." : (initial ? "Update" : "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Test Results Panel ---
function TestResultsPanel() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/test/results").then((r) => { setResults(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this test result?")) return;
    await api.delete(`/test/results/${id}`);
    load();
  };

  const total = results.length;
  const avgPct = total > 0 ? Math.round(results.reduce((s, r) => s + r.percentage, 0) / total) : null;
  const passRate = total > 0 ? Math.round(results.filter((r) => r.percentage >= 60).length / total * 100) : null;
  const topicCounts = results.reduce((acc, r) => { acc[r.topic_label] = (acc[r.topic_label] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="test-results-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Tests", value: total },
          { label: "Avg Score", value: avgPct !== null ? `${avgPct}%` : "—" },
          { label: "Pass Rate (≥60%)", value: passRate !== null ? `${passRate}%` : "—" },
          { label: "Topics Attempted", value: Object.keys(topicCounts).length || "—" },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white border border-border rounded-2xl p-4 text-center">
            <div className="text-2xl font-bold text-[#7C3AED]">{value}</div>
            <div className="text-xs text-[#5C6661] mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="test-results-table">
            <thead className="bg-[#F2EFEB] text-[#5C6661] text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-5 py-3">Date & Time</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Mobile</th>
                <th className="text-left px-5 py-3">Topic</th>
                <th className="text-center px-5 py-3">Score</th>
                <th className="text-center px-5 py-3">%</th>
                <th className="text-right px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center py-10 text-[#5C6661]">Loading…</td></tr>}
              {!loading && results.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-[#5C6661]">
                    <BrainCircuit className="w-10 h-10 mx-auto opacity-30 mb-3" />
                    No test results yet. Students can take the AI Test Agent quiz on the home page.
                  </td>
                </tr>
              )}
              {results.map((r) => (
                <>
                  <tr key={r.id} className="border-t border-border hover:bg-[#F9F8F6]" data-testid={`test-row-${r.id}`}>
                    <td className="px-5 py-3 text-[#5C6661] text-xs whitespace-nowrap">
                      {new Date(r.completed_at).toLocaleString("en-IN")}
                    </td>
                    <td className="px-5 py-3 font-medium text-xs">{r.email}</td>
                    <td className="px-5 py-3 font-mono text-xs">{r.mobile}</td>
                    <td className="px-5 py-3">
                      <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800 font-medium">
                        {r.topic_label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center font-semibold">{r.score}/{r.total}</td>
                    <td className="px-5 py-3 text-center">
                      <span className={`font-bold ${r.percentage >= 60 ? "text-green-600" : "text-red-500"}`}>
                        {r.percentage}%
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                          className="text-[#7C3AED] hover:bg-purple-50 p-2 rounded-lg"
                          title="View details"
                          data-testid={`expand-result-${r.id}`}
                        >
                          {expanded === r.id ? <ChevronUp className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => remove(r.id)}
                          className="text-red-600 hover:bg-red-50 p-2 rounded-lg"
                          data-testid={`delete-result-${r.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr key={`${r.id}-detail`} className="border-t border-border bg-[#F9F8F6]">
                      <td colSpan={7} className="px-5 py-4">
                        <div className="text-xs font-semibold text-[#5C6661] uppercase tracking-widest mb-3">
                          Answer Breakdown — {r.topic_label}
                        </div>
                        <div className="space-y-2">
                          {(r.details || []).map((d, i) => (
                            <div key={i} className={`flex items-start gap-3 rounded-xl px-4 py-2.5 ${d.is_correct ? "bg-green-50 border border-green-100" : "bg-red-50 border border-red-100"}`}>
                              <span className="shrink-0 text-sm">{d.is_correct ? "✅" : "❌"}</span>
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-[#222B26]">Q{i + 1}. {d.question}</p>
                                <p className="text-xs mt-0.5">
                                  <span className={d.is_correct ? "text-green-700" : "text-red-600"}>
                                    Selected: <strong>{String.fromCharCode(65 + d.selected)}. {d.options[d.selected]}</strong>
                                  </span>
                                  {!d.is_correct && (
                                    <span className="text-green-700 ml-3">
                                      ✓ Correct: <strong>{String.fromCharCode(65 + d.correct)}. {d.options[d.correct]}</strong>
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
